import React, { useState, useRef } from "react";
import { useEffect } from "react";
import axios from "axios";

export default function AgencyKyc({ forType = "agency" }) {
  const [kycStatus, setKycStatus] = useState("");
  const [kycDetails, setKycDetails] = useState(null);
  const [numberVerified, setNumberVerified] = useState(false);
  const [callingNumber, setCallingNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [msg, setMsg] = useState("");

  // Get agency info
  const agency = sessionStorage.getItem(forType);
  const newAgency = JSON.parse(agency);
  let agencyName = newAgency?.name || "";
  let agencyCode = newAgency?.uniqueId || "";

  useEffect(() => {
    const fetchKyc = async () => {
      if (!agencyCode) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASEURL1}/agency/myKycStatus`, {
          params: { agencyCode, forType }
        });
        if (res.data.success) {
          setKycStatus(res.data.status);
          setKycDetails(res.data.kycDetails || null);
          setNumberVerified(res.data.numberVerified || false);
          setCallingNumber(res.data.kycDetails?.callingNumber || "");
          setAgreement(res.data.doc || null);
        }
      } catch (e) { }
    };
    fetchKyc();
  }, [agencyCode, forType]);

  // Number verification handlers
  const handleSendOtp = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await axios.post(`${process.env.REACT_APP_BASEURL1}/agency/sendNumberOtp`, { uniqueId: agencyCode, callingNumber, forType });
      setMsg(res.data.message);
      if (res.data.success) setOtpSent(true);
    } catch (e) {
      setMsg("Error sending OTP");
    }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await axios.post(`${process.env.REACT_APP_BASEURL1}/agency/verifyNumberOtp`, { callingNumber, otp, forType });
      setMsg(res.data.message);
      if (res.data.success) setNumberVerified(true);
    } catch (e) {
      setMsg("Error verifying OTP");
    }
    setLoading(false);
  };
  const [step, setStep] = useState(1);
  const initialForm = JSON.parse(localStorage.getItem("kycFormData")) || {
    bankAccountHolderName: "",
    bankAccountNumber: "",
    bankAccountIFSC: "",
    bankName: "",
    aadhaarNumber: "",
    panNumber: "",
    aadhaarFront: null,
    aadhaarBack: null,
    pan: null,
    video: null,
    otp: "",
    otpTxnId: ""
  };

  const [form, setForm] = useState(initialForm);
  useEffect(() => {
    const dataToSave = {
      ...form,
      aadhaarFront: null,
      aadhaarBack: null,
      pan: null,
      video: null,
      signature: null,
    };
    localStorage.setItem("kycFormData", JSON.stringify(dataToSave));
  }, [form]);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const videoRef = useRef();

  const [agreement, setAgreement] = useState(null);


  // Size limits (in bytes)
  const IMAGE_LIMIT = 100 * 1024 * 1024;
  const VIDEO_LIMIT = 100 * 1024 * 1024;

  // File validation handler
  const handleChange = async (e) => {
    setError("");
    if (e.target.files) {
      const file = e.target.files[0];
      if (!file) return;
      if (["aadhaarFront", "aadhaarBack", "pan", "signature"].includes(e.target.name)) {
        // Image check
        if (file.size > IMAGE_LIMIT) {
          setError("Image size should not exceed 100 MB.");
          return;
        }
      }
      if (e.target.name === "video") {
        // Video check
        if (file.size > VIDEO_LIMIT) {
          setError("Video size should not exceed 100 MB.");
          return;
        }
        // Duration check (async, after file loaded)
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.onloadedmetadata = function () {
          window.URL.revokeObjectURL(url);
          if (video.duration > 600) {
            setError("Video duration should not exceed 10 minutes.");
            setForm((prev) => ({ ...prev, video: null }));
          } else {
            setForm((prev) => ({ ...prev, [e.target.name]: file }));
          }
        };
        video.src = url;
        return;
      }
      setForm({ ...form, [e.target.name]: file });
    } else {
      setForm({ ...form, [e.target.name]: e.target.value });
    }
  };

  // PAN Number validation
  function isValidPanCardNo(panCardNo) {
    let regex = new RegExp(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/);
    if (!panCardNo) return false;
    return regex.test(panCardNo);
  }

  const handleInitiate = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    // Aadhaar validation: 12 digits
    if (!/^\d{12}$/.test(form.aadhaarNumber)) {
      setError("Aadhaar number must be exactly 12 digits.");
      setLoading(false);
      return;
    }
    // PAN validation
    if (!isValidPanCardNo(form.panNumber)) {
      setError("Invalid PAN number.");
      setLoading(false);
      return;
    }
    // File validations
    if (
      !form.aadhaarNumber ||
      !form.panNumber ||
      !form.aadhaarFront ||
      !form.aadhaarBack ||
      !form.pan ||
      !form.video
    ) {
      setError("All fields are required.");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("bankAccountHolderName", form.bankAccountHolderName);
    data.append("bankAccountNumber", form.bankAccountNumber);
    data.append("bankAccountIFSC", form.bankAccountIFSC);
    data.append("bankName", form.bankName);
    data.append("aadhaarNumber", form.aadhaarNumber);
    data.append("panNumber", form.panNumber);
    data.append("address", form.address);
    data.append("aadhaarFront", form.aadhaarFront);
    data.append("aadhaarBack", form.aadhaarBack);
    data.append("pan", form.pan);
    data.append("video", form.video);
    data.append("signature", form.signature);
    data.append("agencyCode", agencyCode);
    data.append("agencyName", agencyName);
    data.append("forType", forType);

    try {
      const res = await fetch(`${process.env.REACT_APP_BASEURL2}/agency/submitKyc`, {
        method: "POST",
        body: data
      });
      const result = await res.json();
      if (result.success === 1 && result.otpTxnId) {
        setForm((prev) => ({ ...prev, otpTxnId: result.otpTxnId }));
        setStep(2);
        setMessage(result.message);
      } else {
        setError("Wait for sometime as your previous OTP is already generated");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);

    // Aadhaar validation: 12 digits
    if (!/^\d{12}$/.test(form.aadhaarNumber)) {
      setError("Aadhaar number must be exactly 12 digits.");
      setLoading(false);
      return;
    }
    // PAN validation
    if (!isValidPanCardNo(form.panNumber)) {
      setError("Invalid PAN number. Format: 5 uppercase letters, 4 digits, 1 uppercase letter (e.g. ABCDE1234F)");
      setLoading(false);
      return;
    }

    const data = new FormData();
    data.append("bankAccountHolderName", form.bankAccountHolderName);
    data.append("bankAccountNumber", form.bankAccountNumber);
    data.append("bankAccountIFSC", form.bankAccountIFSC);
    data.append("bankName", form.bankName);
    data.append("aadhaarNumber", form.aadhaarNumber);
    data.append("panNumber", form.panNumber);
    data.append("address", form.address);
    data.append("aadhaarFront", form.aadhaarFront);
    data.append("aadhaarBack", form.aadhaarBack);
    data.append("signature", form.signature);
    data.append("pan", form.pan);
    data.append("video", form.video);
    data.append("agencyCode", agencyCode);
    data.append("agencyName", agencyName);
    data.append("otp", form.otp);
    data.append("otpTxnId", form.otpTxnId);
    data.append("forType", forType);

    try {
      const res = await fetch(`${process.env.REACT_APP_BASEURL2}/agency/submitKyc`, {
        method: "POST",
        body: data
      });
      const result = await res.json();
      if (result.success === 1) {
        setMessage(result.message || "KYC submitted successfully.");
        setStep(3);
        localStorage.removeItem("kycFormData"); // clear localStorage

      } else {
        setError(result.message || "OTP verification failed.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
    setLoading(false);
  };

  // Styles
  const containerStyle = {
    maxWidth: 420,
    margin: "40px auto",
    padding: 32,
    background: "#f8fafd",
    borderRadius: 18,
    boxShadow: "0 8px 40px 0 rgba(0,0,0,0.11)",
    fontFamily: "Inter, Arial, sans-serif"
  };
  const titleStyle = {
    fontSize: 28,
    marginBottom: 18,
    color: "#183153",
    textAlign: "center",
    fontWeight: 700,
    letterSpacing: 0.2
  };
  const labelStyle = {
    display: "block",
    marginTop: 14,
    marginBottom: 7,
    color: "#314366",
    fontSize: 16,
    fontWeight: 500
  };
  const inputStyle = {
    width: "100%",
    padding: "10px 12px",
    marginBottom: 6,
    border: "1.5px solid #b8c6e3",
    borderRadius: 8,
    fontSize: 15,
    background: "#fff"
  };
  const buttonStyle = {
    width: "100%",
    padding: "12px",
    marginTop: 18,
    background: "#3576f6",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 17,
    cursor: loading ? "not-allowed" : "pointer",
    boxShadow: "0 2px 12px #3576f62b"
  };
  const errorStyle = {
    background: "#ffe9e8",
    color: "#c1272d",
    padding: "10px 16px",
    borderRadius: 6,
    fontSize: 15,
    marginBottom: 12,
    border: "1px solid #ffc4c4"
  };
  const messageStyle = {
    background: "#ebffef",
    color: "#20784b",
    padding: "10px 16px",
    borderRadius: 6,
    fontSize: 15,
    marginBottom: 12,
    border: "1px solid #9febc4"
  };

  // ...existing code above...

  return (
    <div style={containerStyle}>
      <div style={titleStyle}>KYC Verification</div>
      {agreement && (
        <div style={{ marginBottom: 18, display: "flex", gap: 16 }}>
          <button
            style={{ background: "#3576f6", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontWeight: 600, cursor: "pointer" }}
            onClick={() => window.open(`${process.env.REACT_APP_BASEURL1}/${agreement.agreementPath.replace(/\\/g, "/")}`, "_blank")}
          >
            View Agreement
          </button>
          <button
            style={{ background: "#3576f6", color: "#fff", border: "none", borderRadius: 6, padding: "8px 18px", fontWeight: 600, cursor: "pointer" }}
            onClick={() => window.open(`${process.env.REACT_APP_BASEURL1}/${agreement.termsConditionPath.replace(/\\/g, "/")}`, "_blank")}
          >
            View Terms & Conditions
          </button>
          <button
            onClick={() => {
              localStorage.removeItem("kycFormData");
              setForm(initialForm);
            }}
            style={{ ...buttonStyle, background: "#f5222d", marginTop: 12 }}
          >
            Reset Form
          </button>

        </div>
      )}
      {/* 1. Mobile Number Verification */}
      <div
        style={{
          marginBottom: 24,
          padding: 16,
          borderRadius: 8,
          background: numberVerified ? "#e6ffed" : "#fffbe6",
          border: numberVerified ? "1px solid #b7eb8f" : "1px solid #ffe58f",
          color: numberVerified ? "#389e0d" : "#ad8b00",
          fontWeight: 500,
        }}
      >
        <div>
          <span>
            <b>Mobile Number Verification:</b>{" "}
            {numberVerified ? "Verified" : "Not Verified"}
          </span>
        </div>
        {!numberVerified && (
          <div style={{ marginTop: 12 }}>
            <input
              type="text"
              placeholder="Enter Mobile Number"
              value={callingNumber}
              onChange={e => setCallingNumber(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ccc",
                borderRadius: 6,
                fontSize: 15,
                marginRight: 8,
                width: 180,
              }}
              disabled={otpSent}
            />
            {!otpSent ? (
              <button
                onClick={handleSendOtp}
                style={{
                  background: "#1677ff",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "8px 18px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send OTP"}
              </button>
            ) : (
              <>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={e => setOtp(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    fontSize: 15,
                    marginRight: 8,
                    width: 120,
                  }}
                />
                <button
                  onClick={handleVerifyOtp}
                  style={{
                    background: "#52c41a",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "8px 18px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify OTP"}
                </button>
              </>
            )}
            {msg && (
              <div style={{ marginTop: 8, color: "#cf1322" }}>{msg}</div>
            )}
          </div>
        )}
      </div>

      {/* 2. KYC Status */}
      <div
        style={{
          marginBottom: 24,
          padding: 16,
          borderRadius: 8,
          background:
            kycStatus === "approved"
              ? "#e6ffed"
              : kycStatus === "pending"
                ? "#fffbe6"
                : "#fff1f0",
          border:
            kycStatus === "approved"
              ? "1px solid #b7eb8f"
              : kycStatus === "pending"
                ? "1px solid #ffe58f"
                : "1px solid #ffa39e",
          color:
            kycStatus === "approved"
              ? "#389e0d"
              : kycStatus === "pending"
                ? "#ad8b00"
                : "#cf1322",
          fontWeight: 500,
        }}
      >
        <div>
          <span>
            <b>KYC Status:</b>{" "}
            {kycStatus === "approved"
              ? "Approved"
              : kycStatus === "pending"
                ? "Pending (wait 24h to 1 week, contact seniors if too long)"
                : kycStatus === "rejected"
                  ? "Rejected. Fill the Form Again"
                  : "Not Submitted"}
          </span>
        </div>
      </div>

      {/* 3. KYC Form (only if number verified and KYC not approved/pending) */}
      {numberVerified && (kycStatus === "rejected" || kycStatus === "no_kyc") && (
        <>
          {step === 1 && (
            <form onSubmit={handleInitiate} autoComplete="off">
              <label style={labelStyle}>bankAccountHolderName</label>
              <input
                type="text"
                name="bankAccountHolderName"
                placeholder="Enter Bank Account Holder Name"
                value={form.bankAccountHolderName}
                onChange={handleChange}
                style={inputStyle}
                required
              />


              <label style={labelStyle}>Bank Account Number</label>
              <input
                type="number"
                name="bankAccountNumber"
                placeholder="Enter Bank Account Number"
                value={form.bankAccountNumber}
                onChange={handleChange}
                style={inputStyle}
                required
                pattern="\d{9,18}"
                maxLength={18}
              />

              <label style={labelStyle}>Bank Account IFSC</label>
              <input
                type="text"
                name="bankAccountIFSC"
                placeholder="Enter Bank Account IFSC"
                value={form.bankAccountIFSC}
                onChange={handleChange}
                style={inputStyle}
                required
                pattern="[A-Z]{4}0[A-Z0-9]{6}"
                maxLength={11}
              />
              <label style={labelStyle}>Bank Name</label>
              <input
                type="text"
                name="bankName"
                placeholder="Enter Bank Name"
                value={form.bankName}
                onChange={handleChange}
                style={inputStyle}
                required
                maxLength={50}
              />
              <label style={labelStyle}>Aadhaar Number</label>
              <input
                type="number"
                name="aadhaarNumber"
                placeholder="Enter Aadhaar Number"
                value={form.aadhaarNumber}
                onChange={handleChange}
                style={inputStyle}
                required
                pattern="\d{12}"
                maxLength={12}
              />

              <label style={labelStyle}>PAN Number</label>
              <input
                type="text"
                name="panNumber"
                placeholder="Enter PAN Number"
                value={form.panNumber}
                onChange={handleChange}
                style={inputStyle}
                required
                maxLength={10}
              />


              <label style={labelStyle}>Aadhaar Front (max 5MB)</label>
              <input
                type="file"
                name="aadhaarFront"
                accept="image/*"
                style={inputStyle}
                onChange={handleChange}
                required
              />

              <label style={labelStyle}>Aadhaar Back (max 5MB)</label>
              <input
                type="file"
                name="aadhaarBack"
                accept="image/*"
                style={inputStyle}
                onChange={handleChange}
                required
              />

              <label style={labelStyle}>PAN Card (max 5MB)</label>
              <input
                type="file"
                name="pan"
                accept="image/*"
                style={inputStyle}
                onChange={handleChange}
                required
              />

              <label style={labelStyle}>KYC Video (max 5 min, 25MB)</label>
              <input
                type="file"
                name="video"
                accept="video/*"
                style={inputStyle}
                onChange={handleChange}
                required
                ref={videoRef}
              />
              <label style={labelStyle}>Signature With White background</label>
              <input
                type="file"
                name="signature"
                accept="image/*"
                style={inputStyle}
                onChange={handleChange}
                required
              />

              <button type="submit" style={buttonStyle} disabled={loading}>
                {loading ? "Submitting..." : "Submit & Get OTP"}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={handleVerify} autoComplete="off">
              <label style={labelStyle}>Enter OTP</label>
              <input
                type="text"
                name="otp"
                placeholder="Enter OTP"
                value={form.otp}
                onChange={handleChange}
                style={inputStyle}
                required
                maxLength={6}
              />
              <button type="submit" style={buttonStyle} disabled={loading}>
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
            </form>
          )}

          {step === 3 && (
            <div style={{ ...messageStyle, color: "#183153", background: "#e3eefd", border: "1px solid #aac4f7" }}>
              <b>KYC Submitted!</b><br />Your details have been sent for admin approval.
            </div>
          )}
        </>
      )}
      {error && (
        <div style={errorStyle}>
          <b>Error:</b> {error}
        </div>
      )}
      {/* If KYC is pending or approved, do not show form */}
      {!numberVerified && (
        <div style={{ textAlign: "center", color: "#888", marginTop: 24 }}>
          <b>Verify your mobile number to proceed with KYC.</b>
        </div>
      )}


    </div>
  );
}
